import { graphql } from 'gatsby'
import React, { useRef, useEffect, useState } from 'react'

import Helmet from 'react-helmet'

import PortableText from '@sanity/block-content-to-react'

import Layout from '../components/Layout'
import StudentList from '../components/StudentList'
import StudentGrid from '../components/StudentGrid'
import Projects from '../components/Project'
import StudentHeader from '../components/StudentHeader'
import ArtworkNav from '../components/ArtworkNav'
import { TweenMax, Power3, gsap } from 'gsap'
import { CSSRulePlugin } from 'gsap/CSSRulePlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Bio from '../components/Bio'
import Nav from '../components/Nav'
import NextStudent from '../components/NextStudent'

gsap.registerPlugin(CSSRulePlugin, ScrollTrigger, gsap)

export default function SingleStudentPage ({ data: { student, students }, pageContext }) {
  const [open, setOpen] = useState(false)
  const pageTitle = 'CCA MFA 2021 | ' + student.first_name + ' ' + student.last_name

  return (
    <Layout students={students.nodes} student={student}>
			 <Helmet>
        <title>{pageTitle}</title>

        <meta name="description" content="CCA MFA Fine Arts Class of 2021" />
        <meta property="og:title" content="CCA MFA Fine Arts Class of 2021"/>
        {/* <meta property="og:url" content={student.first_name} {student.last_name}/> */}
        <meta property="og:image" content={student.hero_image.asset.url}/>

        <meta property="og:url" content="https://cca-mfa-2021-01.netlify.app/"/>
        <meta property="og:type" content="website"/>
        <meta property="og:title" content={pageTitle}/>
        <meta property="og:description" content="CCA MFA 2021"/>

        <meta name="twitter:card" content="summary_large_image"/>
        <meta property="twitter:domain" content="cca-mfa-2021-01.netlify.app"/>
        <meta property="twitter:url" content="https://cca-mfa-2021-01.netlify.app/"/>
        <meta name="twitter:title" content={pageTitle}/>
        <meta name="twitter:description" content="CCA MFA 2021"/>
        <meta name="twitter:image" content={student.hero_image.asset.url}/>

      </Helmet>
      <Nav students={students.nodes}/>
      <StudentHeader student={student} />
      <ArtworkNav student={student} artwork={student.work} />
      <Projects artwork={student.work} />

      <Bio student={student}/>

      <NextStudent students={students.nodes} next = {pageContext.next}/>
      {/* <StudentList students={students.nodes} /> */}
    </Layout>
  )
}

export const query = graphql`
  query($slug: String!) {
		studentEdge: allSanityPerson(sort: {fields: [last_name], order: ASC}) {
			edges {
				next {
					slug {
						current
					}
				}
				node {
					id
					slug {
						current
					}
				}
			}
		}
    students: allSanityPerson(sort: { fields: [last_name], order: ASC }) {
      nodes {
        first_name
        hero_image {
          asset {
            url
            fluid(maxWidth: 400) {
              src
              ...GatsbySanityImageFluid
            }
          }
        }
        last_name
        id
        slug {
          current
        }
      }
    }
    student: sanityPerson(slug: { current: { eq: $slug } }) {
      id
      first_name
      last_name
      hero_image {
        asset {
          url
          fixed(width: 400) {
						src
            ...GatsbySanityImageFixed
          }
          fluid(maxWidth: 400) {
						src
            ...GatsbySanityImageFluid
          }
        }
      }
      file {
        asset {
          url
        }
      }
      work {
        _key
        title
        year
        width
        height
        depth
        materials
        _rawDescription
        slides {
          ... on SanityFullBleed {
            _key
            _type
            asset {
              source{
                  url
                }
              fluid(maxWidth: 800) {
                ...GatsbySanityImageFluid
              }
              fixed(width: 800) {
                ...GatsbySanityImageFixed
              }
            }
          }
          ... on SanityGrid2X2 {
            _key
            _type
            image_01 {
              _key
              asset {
                url
                source{
                  url
                }
                fluid(maxWidth: 400) {
                  src
                  ...GatsbySanityImageFluid
                }
                fixed(width: 800) {
                  ...GatsbySanityImageFixed

                }
              }
            }
            image_02 {
              _key
              asset {
                source{
                  url
                }
                fluid(maxWidth: 400) {
                  src
                  ...GatsbySanityImageFluid
                }
                fixed(width: 800) {
                  ...GatsbySanityImageFixed
                }
              }
            }
          }
          ... on SanityWorkVideo {
            _key
            _type
            url
          }
          ... on SanityGrid4X4 {
            _key
            _type
            grid_image_01 {
              asset {
                source{
                  url
                }
                fluid(maxWidth: 400) {
                  src
                  ...GatsbySanityImageFluid
                }
                fixed(width: 400) {
                  ...GatsbySanityImageFixed
                }
              }
            }
            grid_image_02 {
              asset {
                source{
                  url
                }
                fluid(maxWidth: 400) {
                  src
                  ...GatsbySanityImageFluid
                }
                fixed(width: 400) {
                  ...GatsbySanityImageFixed
                }
              }
            }
            grid_image_03 {
              asset {
                source{
                  url
                }
                fluid(maxWidth: 400) {
                  src
                  ...GatsbySanityImageFluid
                }
                fixed(width: 400) {
                  ...GatsbySanityImageFixed
                }
              }
            }
            grid_image_04 {
              asset {
                source{
                  url
                }
                fluid(maxWidth: 400) {
                  src
                  ...GatsbySanityImageFluid
                }
                fixed(width: 400) {
                  ...GatsbySanityImageFixed
                }
              }
            }
          }
          ... on SanityWriting {
            _rawRichText
            _key
          }

          ... on SanityInsetImage {
            _key
            _type
            size
            alt_text
            asset {
              source{
                  url
                }
                fluid(maxWidth: 500) {
                  src
                  ...GatsbySanityImageFluid
                }
              fixed(width: 500) {
                ...GatsbySanityImageFixed
              }
              
            }
          }
        }
      }
      links {
        _key
        link_text
        url
      }
			slug {
				current
			}
      _rawBio
      _rawArtistStatement
    }
  }
`
